import React from "react"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { WithClassName } from "shared/types/WithClassName"
import { FormProvider as BaseFormProvider } from "shared/ui/form/FormContext"

import { ProductPositionFormModel } from "features/product-position-management/types/ProductPositionFormModel"

import { ProductPositionFormAttachmentsProvider } from "./context/ProductPositionFormAttachmentsContext"
import { ProductPositionFormCoreProvider } from "./contexts/ProductPositionFormCoreContext"
import { ProductPositionFormFeaturesProvider } from "./contexts/ProductPositionFormFeaturesContext"
import { ProductPositionFormLoadingProvider } from "./contexts/ProductPositionFormLoadingContext"
import { ProductPositionFormPriceProvider } from "./contexts/ProductPositionFormPriceContext"
import { ProductPositionFormProductInformationProvider } from "./contexts/ProductPositionFormProductInformationContext"
import { ProductPositionFormSelectionProvider } from "./contexts/ProductPositionFormSelectionContext"
import { useProductPositionFormViewModel } from "./model/useProductPositionFormViewModel"
import "./styles/_product-position-form.scss"
import { ProductPositionConfigurationButtons } from "./ui/ProductPositionConfigurationButtons"
import { ProductPositionFormClarificationRequest } from "./ui/ProductPositionFormClarificationRequest"
import { ProductPositionFormTabs } from "./ui/ProductPositionFormTabs"
import { ProductPositionInstallationReferences } from "./ui/ProductPositionInstallationReferences"
import { ProductPositionDisplayPositionInput } from "./ui/inputs/ProductPositionDisplayPositionInput"
import { ProductPositionIsAlternativeInput } from "./ui/inputs/ProductPositionIsAlternativeInput"
import { ProductPositionPositionInput } from "./ui/inputs/ProductPositionPositionInput"
import { ProductPositionPriceGroupInput } from "./ui/inputs/ProductPositionPriceGroupInput"
import { ProductPositionPriceInput } from "./ui/inputs/ProductPositionPriceInput"
import { ProductPositionProjectInput } from "./ui/inputs/ProductPositionProjectInput"
import { ProductPositionQuantityInput } from "./ui/inputs/ProductPositionQuantityInput"
import { ProductPositionRemarkInput } from "./ui/inputs/ProductPositionRemarkInput"
import { ProductPositionRoomInput } from "./ui/inputs/ProductPositionRoomInput"
import { ProductPositionSpecialPriceGroupInput } from "./ui/inputs/ProductPositionSpecialPriceGroupInput"
import { ProductPositionTotalPrice } from "./ui/inputs/ProductPositionTotalPrice"
import { ProductPositionVariantKeyInput } from "./ui/inputs/ProductPositionVariantKeyInput"

export interface ProductPositionFormProps
  extends ProductPositionFormModel,
    WithClassName {
  autoFocus?: boolean
  children?: React.ReactNode
}

const ProductPositionFormRoot = React.memo(
  (props: ProductPositionFormProps) => {
    const { id, className, children, formOptions, autoFocus } = props
    const { t } = useTranslation()
    const {
      form,
      coreContext,
      loadingContext,
      priceContext,
      selectionContext,
      featuresContext,
      productInformationContext,
      attachmentsContext
    } = useProductPositionFormViewModel({
      ...props,
      formOptions: { ...formOptions, autoFocus }
    })

    return (
      <BaseFormProvider
        isReadonly={coreContext.isReadonly}
        isDisabled={coreContext.isDisabled}
      >
        <ProductPositionFormProductInformationProvider
          value={productInformationContext}
        >
          <ProductPositionFormCoreProvider value={coreContext}>
            <ProductPositionFormLoadingProvider value={loadingContext}>
              <ProductPositionFormPriceProvider value={priceContext}>
                <ProductPositionFormSelectionProvider value={selectionContext}>
                  <ProductPositionFormFeaturesProvider value={featuresContext}>
                    <ProductPositionFormAttachmentsProvider
                      value={attachmentsContext}
                    >
                      <FormProvider {...form}>
                        <form
                          id={id}
                          data-testid="product-position-form"
                          aria-label={t("ProductPosition.Actions.Update")}
                          className={classNames(
                            "product-position-form",
                            className
                          )}
                          onSubmit={coreContext.onSubmit}
                        >
                          {children}
                        </form>
                      </FormProvider>
                    </ProductPositionFormAttachmentsProvider>
                  </ProductPositionFormFeaturesProvider>
                </ProductPositionFormSelectionProvider>
              </ProductPositionFormPriceProvider>
            </ProductPositionFormLoadingProvider>
          </ProductPositionFormCoreProvider>
        </ProductPositionFormProductInformationProvider>
      </BaseFormProvider>
    )
  }
)

export const ProductPositionForm = Object.assign(ProductPositionFormRoot, {
  Tabs: ProductPositionFormTabs,
  VariantKeyInput: ProductPositionVariantKeyInput,
  ConfigurationButtons: ProductPositionConfigurationButtons,
  ProjectInput: ProductPositionProjectInput,
  RoomInput: ProductPositionRoomInput,
  DisplayPositionInput: ProductPositionDisplayPositionInput,
  PositionInput: ProductPositionPositionInput,
  IsAlternativeInput: ProductPositionIsAlternativeInput,
  QuantityInput: ProductPositionQuantityInput,
  PriceInput: ProductPositionPriceInput,
  PriceGroupInput: ProductPositionPriceGroupInput,
  TotalPrice: ProductPositionTotalPrice,
  SpecialPriceGroupInput: ProductPositionSpecialPriceGroupInput,
  RemarkInput: ProductPositionRemarkInput,
  InstallationReferences: ProductPositionInstallationReferences,
  ClarificationRequest: ProductPositionFormClarificationRequest
})
